import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { AnyAction, Dispatch } from 'redux';
import { geocodingQueue } from '../../utils/geocodingQueue';
import { showToast } from '../../utils/toastHelper';
import { calculateDistanceFromHome } from '../../utils/distanceUtils';
import { HOME_COORDINATES } from '../../constants/appConfig';
import { calculateOrderPricing } from '../../utils/pricingUtils';
import type { Order } from '../../types/order';
import type { RootState } from '../store';

/**
 * Redux middleware that automatically geocodes orders when they're created or updated.
 *
 * Features:
 * - Intercepts order creation and update actions
 * - Detects orders missing coordinates (no manualGeocode field)
 * - Queues addresses for geocoding without blocking order creation
 * - Updates orders with coordinates when geocoding completes
 * - Handles multiple rapid order creations via rate-limited queue
 */
export const geocodingMiddleware = ((store: MiddlewareAPI<Dispatch, RootState>) =>
  (next: Dispatch) =>
  (action: AnyAction) => {
    // Execute the action first - never block order creation
    const result = next(action);

    // Type guards for runtime safety
    const hasType = (act: unknown): act is { type: string } => {
      return typeof act === 'object' && act !== null && 'type' in act;
    };

    const hasPayload = (act: unknown): act is { payload: any } => {
      return typeof act === 'object' && act !== null && 'payload' in act;
    };

    // Only process order actions with payloads
    if (!hasType(action) || !hasPayload(action)) {
      return result;
    }

    // Check if this is an order creation or update action
    const isAddOrder = action.type === 'orders/addOrder';
    const isUpdateOrder = action.type === 'orders/updateOrder';

    if (!isAddOrder && !isUpdateOrder) {
      return result;
    }

    // Extract the order from the payload
    const order = action.payload as Order;

    // Skip if order already has coordinates (manualGeocode is set)
    if (order.manualGeocode) {
      console.log(`[Geocoding Middleware] Order ${order.id} already has coordinates, skipping geocoding`);
      return result;
    }

    // Skip if order doesn't have a valid address
    if (!order.streetAddress || !order.city || !order.province) {
      console.log(`[Geocoding Middleware] Order ${order.id} has incomplete address, skipping geocoding`);
      return result;
    }

    console.log(`[Geocoding Middleware] Queueing geocoding for order ${order.id}`);

    // Queue the geocoding request asynchronously
    geocodingQueue
      .enqueue(order.streetAddress, order.addressLine2, order.city, order.province, order.postalCode)
      .then(result => {
        if (result) {
          console.log(`[Geocoding Middleware] Geocoding successful for order ${order.id}:`, result);

          // Get the current state to check if order still exists
          const currentState = store.getState();
          const currentOrder = currentState.orders.orders.find(o => o.id === order.id);

          // Only update if order still exists and doesn't have manual geocode set
          if (currentOrder && !currentOrder.manualGeocode) {
            // Calculate distance from home (only for Delivery orders)
            let distanceFromHome: number | undefined;
            if (currentOrder.deliveryType === 'Delivery') {
              distanceFromHome = calculateDistanceFromHome(HOME_COORDINATES, { lat: result.lat, lng: result.lng });
              console.log(`[Geocoding Middleware] Calculated distance: ${distanceFromHome} km`);
            }

            // Dispatch an update action with the geocoded coordinates AND distance
            store.dispatch({
              type: 'orders/updateOrder',
              payload: {
                ...currentOrder,
                manualGeocode: {
                  lat: result.lat,
                  lng: result.lng,
                  setBy: 'auto-geocoding',
                  setAt: new Date().toISOString()
                },
                distanceFromHome
              }
            });

            console.log(
              `[Geocoding Middleware] Order ${order.id} updated with coordinates${distanceFromHome ? ` and distance (${distanceFromHome} km)` : ''}`
            );

            // NEW: If order is finalized, recalculate pricing with new distance
            if (currentOrder.isFinalized && distanceFromHome !== undefined) {
              console.log(
                `[Geocoding Middleware] Order ${order.id} is finalized, recalculating pricing with distance ${distanceFromHome} km`
              );

              // Get current pricing state
              const pricingState = store.getState().pricing;

              // Recalculate pricing
              const updatedPricing = calculateOrderPricing(
                {
                  ...currentOrder,
                  distanceFromHome
                },
                pricingState
              );

              console.log(`[Geocoding Middleware] Recalculated pricing:`);
              console.log(`  - Delivery Fee: ${updatedPricing.deliveryFee.toFixed(2)}`);
              console.log(`  - Total: ${updatedPricing.total.toFixed(2)}`);

              // Update order again with new pricing
              store.dispatch({
                type: 'orders/updateOrder',
                payload: {
                  ...currentOrder,
                  manualGeocode: {
                    lat: result.lat,
                    lng: result.lng,
                    setBy: 'auto-geocoding',
                    setAt: new Date().toISOString()
                  },
                  distanceFromHome,
                  pricing: updatedPricing // NEW: Update pricing
                }
              });
            }
          } else if (currentOrder?.manualGeocode) {
            console.log(`[Geocoding Middleware] Order ${order.id} already has manual geocode, skipping update`);
          } else {
            console.log(`[Geocoding Middleware] Order ${order.id} no longer exists, skipping update`);
          }
        } else {
          console.warn(`[Geocoding Middleware] Geocoding failed for order ${order.id}`);
          showToast(`Failed to geocode address for ${order.customerName}`, 'warning');
        }
      })
      .catch(error => {
        console.error(`[Geocoding Middleware] Geocoding error for order ${order.id}:`, error);
        showToast(
          `Geocoding error for ${order.customerName}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'error'
        );
      });

    return result;
  }) as Middleware;
